<template>
    <div class="bcid-phone-frame">
        <div class="bcid-phone-screen">
            <!-- Status bar -->
            <div class="bcid-status-bar">
                <span>9:41</span>
                <span class="bcid-status-icons">
                    <i class="fa fa-signal"></i>
                    <i class="fa fa-wifi ml-1"></i>
                    <i class="fa fa-battery-full ml-1"></i>
                </span>
            </div>

            <!-- Caller info -->
            <div class="bcid-caller-section">
                <div class="bcid-caller-name" :style="{ fontSize: nameFontSize }">{{ displayedName }}</div>
                <div class="bcid-caller-number">+1 (800) 555-5555</div>

                <!-- Enhanced: logo + call reason -->
                <template v-if="type === 'enhanced'">
                    <div class="bcid-logo-circle" v-if="!logoLoaded">
                        <div class="bcid-logo-inner">
                            <span class="bcid-logo-text">{{ logoInitials }}</span>
                        </div>
                    </div>
                    <img
                        v-if="logoLoaded"
                        :src="logoUrl"
                        class="bcid-logo-img"
                    />
                    <div class="bcid-call-reason">
                        {{ callReason || 'Customer support' }}
                    </div>
                </template>

                <!-- Standard: just name and number -->
                <template v-else>
                </template>
            </div>

            <!-- Call buttons -->
            <div class="bcid-call-buttons">
                <div class="bcid-btn-call bcid-btn-accept">
                    <i class="fa fa-phone"></i>
                </div>
                <div class="bcid-btn-call bcid-btn-reject">
                    <i class="material-icons" style="font-size: 24px;">call_end</i>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'BrandedCallerIdPreview',

    props: {
        type: {
            type: String,
            default: 'enhanced',
        },
        companyName: {
            type: String,
            default: '',
        },
        callReason: {
            type: String,
            default: '',
        },
        displayLongName: {
            type: String,
            default: '',
        },
        logoUrl: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            logoLoaded: false,
            logoLoadTimeout: null,
        }
    },

    watch: {
        logoUrl(val) {
            this.logoLoaded = false
            clearTimeout(this.logoLoadTimeout)

            if (!val || !val.trim()) return

            try {
                const url = new URL(val)
                if (url.protocol !== 'https:' && url.protocol !== 'http:') return
            } catch {
                return
            }

            const img = new Image()
            img.onload = () => {
                clearTimeout(this.logoLoadTimeout)
                if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                    this.logoLoaded = true
                }
            }
            img.onerror = () => {
                clearTimeout(this.logoLoadTimeout)
                this.logoLoaded = false
            }

            // Timeout after 5 seconds
            this.logoLoadTimeout = setTimeout(() => {
                this.logoLoaded = false
                img.src = ''
            }, 5000)

            img.src = val
        },
    },

    beforeDestroy() {
        clearTimeout(this.logoLoadTimeout)
    },

    computed: {
        displayedName() {
            if (this.type !== 'enhanced' && this.displayLongName && this.displayLongName.trim()) {
                return this.displayLongName
            }
            return this.companyName || 'Your Company'
        },

        logoInitials() {
            const name = this.companyName || 'Your Company'
            return name
                .split(' ')
                .slice(0, 2)
                .map(w => w.charAt(0).toUpperCase())
                .join('')
        },

        nameFontSize() {
            const name = this.displayedName
            const len = name.length
            if (len <= 10) return '24px'
            if (len <= 15) return '20px'
            if (len <= 20) return '16px'
            if (len <= 25) return '13px'
            if (len <= 32) return '11.5px'
            return '10px'
        },
    },
}
</script>

<style scoped>
.bcid-phone-frame {
    width: 265px;
    background: #1a1a1a;
    border-radius: 40px;
    padding: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3), inset 0 0 0 2px #333;
    margin: 0 auto;
}

.bcid-phone-screen {
    background: #2a2a2a;
    border-radius: 30px;
    padding: 16px 20px 24px;
    min-height: 500px;
    display: flex;
    flex-direction: column;
    position: relative;
}

.bcid-status-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 24px;
}

.bcid-status-icons {
    font-size: 11px;
}

.bcid-caller-section {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 16px;
}

.bcid-caller-name {
    color: #fff;
    font-weight: 300;
    text-align: center;
    letter-spacing: 0.5px;
    white-space: nowrap;
    max-width: 100%;
    line-height: 1.2;
    transition: font-size 0.2s ease;
}

.bcid-caller-number {
    color: rgba(255, 255, 255, 0.5);
    font-size: 14px;
    margin-top: 4px;
}

.bcid-logo-circle {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #409eff, #66b1ff);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 20px rgba(64, 158, 255, 0.4);
    margin-top: 24px;
}

.bcid-logo-inner {
    width: 68px;
    height: 68px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
}

.bcid-logo-text {
    color: #fff;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 1px;
}

.bcid-logo-img {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    object-fit: cover;
    margin-top: 24px;
    box-shadow: 0 4px 20px rgba(64, 158, 255, 0.4);
}

.bcid-call-reason {
    color: rgba(255, 255, 255, 0.7);
    font-size: 12px;
    text-align: center;
    margin-top: 8px;
}

.bcid-call-buttons {
    display: flex;
    justify-content: center;
    gap: 48px;
    padding-bottom: 8px;
    padding-top: 32px;
}

.bcid-btn-call {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: #fff;
}

.bcid-btn-accept {
    background: #67c23a;
}

.bcid-btn-reject {
    background: #f56c6c;
}
</style>
